import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { dealsApi } from "@/api/endpoints";
import type { DealDetail } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Note } from "@/components/ui/Note";
import { Panel, PanelActions, PanelHead, Spacer } from "@/components/ui/Panel";
import { useI18n } from "@/i18n/use-i18n";

interface StepProps {
  deal: DealDetail;
  /** Someone else's Vorgang: show it, but offer nothing that writes. */
  readOnly: boolean;
  onChanged: (deal: DealDetail) => void;
  onBack: () => void;
  onOpenPreview: () => void;
  onStartNewDeal: () => void;
  startingNewDeal: boolean;
}

export function ActivationStep({
  deal,
  readOnly,
  onChanged,
  onBack,
  onOpenPreview,
  onStartNewDeal,
  startingNewDeal,
}: StepProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const active = deal.activation?.status === "active";

  const activate = useMutation({
    mutationFn: () => dealsApi.activate(deal.id),
    onSuccess: async () => onChanged(await dealsApi.detail(deal.id)),
  });

  const summary = useQuery({
    queryKey: ["deal-summary", deal.id],
    queryFn: () => dealsApi.summary(deal.id),
    enabled: active,
  });

  // Whole sentences per case rather than one with an interpolated noun: the
  // German "den Kunden" is accusative and cannot be dropped into a slot.
  const customer = deal.customer?.company_name;
  const product = deal.product?.name ?? t("activation.theProduct");
  const headKey = active
    ? customer
      ? "activation.done"
      : "activation.doneNoCustomer"
    : customer
      ? "activation.todo"
      : "activation.todoNoCustomer";

  return (
    <Panel>
      <PanelHead eyebrow={t("activation.eyebrow")} title={t("activation.title")}>
        {t(headKey, { product, customer: customer ?? "" })}
      </PanelHead>

      {!active && (
        <>
          {deal.product?.activation_note && (
            <Note tone="info">{deal.product.activation_note}</Note>
          )}

          {activate.isError && (
            <Note tone="error" className="mt-3.5">
              {(activate.error as Error).message}
            </Note>
          )}

          <PanelActions>
            <Button variant="ghost" onClick={onBack}>
              {t("common.back")}
            </Button>
            {deal.product?.has_demo && (
              <Button variant="ghost" onClick={onOpenPreview}>
                {t("activation.showPreview")}
              </Button>
            )}
            <Spacer />
            <Button
              variant="teal"
              disabled={readOnly}
              busy={activate.isPending}
              onClick={() => activate.mutate()}
            >
              {t("activation.activate")}
            </Button>
          </PanelActions>
        </>
      )}

      {active && (
        <>
          <div className="rounded-card border border-ok/40 bg-ok-wash px-5 py-7 text-center">
            <div className="mx-auto mb-3.5 grid size-12 place-items-center rounded-full bg-ok text-[22px] font-bold text-white">
              ✓
            </div>
            <h2 className="mb-1.5 text-[23px]">{t("activation.completeTitle")}</h2>
            <p className="m-0 text-muted">{t("activation.completeBody")}</p>
          </div>

          {deal.activation?.setup_url && (
            <Note tone="info" className="mt-4">
              {t("activation.setupLink")}
              <span className="mt-1 block break-all font-mono text-[11.5px]">
                {deal.activation.setup_url}
              </span>
            </Note>
          )}

          {summary.data && (
            <div className="mt-4.5 divide-y divide-line-soft overflow-hidden rounded-card border border-line">
              {summary.data.rows.map((row) => (
                <div
                  key={row.label}
                  className="flex flex-wrap justify-between gap-3 px-4 py-2.5 text-[13.5px]"
                >
                  <span className="text-muted">{row.label}</span>
                  <span className="font-semibold">{row.value}</span>
                </div>
              ))}
            </div>
          )}

          <PanelActions>
            <Button variant="ghost" onClick={() => navigate("/")}>
              {t("activation.toOverview")}
            </Button>
            <Spacer />
            <Button busy={startingNewDeal} onClick={onStartNewDeal}>
              {t("activation.newDeal")}
            </Button>
          </PanelActions>
        </>
      )}
    </Panel>
  );
}
